import React, { useRef, useEffect, useState, useMemo } from "react";
import { Row, Col, Container } from "react-bootstrap";
import * as echarts from "echarts";

const EchartsWrapper = ({
  option,
  style = { width: "100%", height: 300 },
  id,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current);
      chartInstance.current.setOption(option);

      const handleResize = () =>
        chartInstance.current && chartInstance.current.resize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        chartInstance.current && chartInstance.current.dispose();
      };
    }
  }, [option]);

  return <div ref={chartRef} id={id} style={style} />;
};
function GraficoDistribucion({
  datos,
  titulo,
  etiquetas = "wrap",
  id = "bar",
  height = 300,
}) {
  const { categorias, valores, maxLen } = useMemo(() => {
    const entries = Object.entries(datos ?? {});
    entries.sort((a, b) => b[1] - a[1]);
    const cats = entries.map(([k]) => String(k));
    const vals = entries.map(([, v]) => Number(v) || 0);
    const maxLenLocal = cats.reduce((m, s) => Math.max(m, s.length), 0);
    return { categorias: cats, valores: vals, maxLen: maxLenLocal };
  }, [datos]);

  const wrapLabel = (label, maxCharsPerLine = 14) => {
    if (!label) return "";
    const words = String(label).split(/\s+/);
    const lines = [];
    let current = "";
    for (const w of words) {
      if ((current + " " + w).trim().length <= maxCharsPerLine) {
        current = (current + " " + w).trim();
      } else {
        if (current) lines.push(current);
        if (w.length > maxCharsPerLine) {
          for (let i = 0; i < w.length; i += maxCharsPerLine) {
            lines.push(w.slice(i, i + maxCharsPerLine));
          }
          current = "";
        } else {
          current = w;
        }
      }
    }
    if (current) lines.push(current);
    return lines.join("\n");
  };

  const axisLabelConfig = useMemo(() => {
    if (etiquetas === "vertical") {
      return {
        interval: 0,
        rotate: 90,
        fontSize: 10,
        margin: 8,
        hideOverlap: true,
      };
    } else {
      return {
        interval: 0,
        rotate: 0,
        fontSize: 11,
        lineHeight: 12,
        margin: 6,
        formatter: (val) => wrapLabel(val, 14),
      };
    }
  }, [etiquetas]);
  const gridBottom = useMemo(() => {
    if (etiquetas === "vertical") {
      return Math.min(80 + Math.max(0, maxLen - 10) * 1, 120);
    } else {
      const avgLines = Math.ceil(Math.min(Math.max(maxLen, 1), 40) / 14);
      return Math.min(24 + avgLines * 12 + 10, 120);
    }
  }, [etiquetas, maxLen]);

  const barOption = useMemo(() => {
    return {
      title: { text: titulo, left: "center" },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params?.[0];
          if (!p) return "";
          return `${p.axisValue}<br/>Cantidad: <b>${p.value}</b>`;
        },
      },
      grid: {
        left: 24,
        right: 16,
        bottom: gridBottom,
        top: 48,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categorias,
        axisLabel: axisLabelConfig,
      },
      yAxis: {
        type: "value",
        name: "Cantidad",
        nameGap: 16,
        nameTextStyle: { fontSize: 12, color: "#616161" },
        axisLabel: { fontSize: 11 },
        splitLine: { lineStyle: { color: "#eeeeee" } },
      },
      series: [
        {
          name: "Cantidad",
          type: "bar",
          data: valores,
          barMaxWidth: 40,
          itemStyle: { color: "#1976d2" },
          label: {
            show: true,
            position: "top",
            fontSize: 11,
            formatter: ({ value }) => (value > 0 ? value : ""),
          },
        },
      ],
    };
  }, [categorias, valores, titulo, axisLabelConfig, gridBottom]);

  return <EchartsWrapper option={barOption} style={{ height }} id={id} />;
}

function GraficoBarrasHorizontal({
  datos,
  titulo,
  color = "#43a047",
  id = "bar-horizontal",
  height = 300,
  compacto = true, 
  yLabelWidth = 110, 
  yFontSize = 10, 
  yOverflow = "truncate", 
}) {
  const { categorias, valores, maxVal } = useMemo(() => {
    const entries = Object.entries(datos ?? {});
    entries.sort((a, b) => b[1] - a[1]);

    const cats = entries.map(([k]) => {
      const n = Number(k);
      return Number.isFinite(n)
        ? `${n} ${n === 1 ? "mes" : "meses"}`
        : String(k);
    });

    const vals = entries.map(([, v]) => Number(v) || 0);
    const mv = vals.length ? Math.max(...vals) : 0;

    return { categorias: cats, valores: vals, maxVal: mv };
  }, [datos]);

  const xMax = useMemo(() => {
    if (!maxVal) return undefined; 
    const padding = Math.ceil(maxVal * 0.1); 
    return maxVal + padding;
  }, [maxVal]);

  const option = useMemo(() => {
    const gridLeft = compacto ? 56 : 80; 
    const gridRight = compacto ? 8 : 16;
    const yAxisLabel = {
      interval: 0,
      width: yLabelWidth,
      overflow: yOverflow,
      lineHeight:
        yOverflow === "break" || yOverflow === "breakAll" ? 12 : undefined,
      fontSize: yFontSize,
      margin: 6,
      align: "right",
    };

    return {
      title: {
        text: titulo,
        left: "center",
        top: 10,
        textStyle: { fontSize: 12 },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params?.[0];
          if (!p) return "";
          return `${p.axisValue}<br/>Alertas: <b>${p.value}</b>`;
        },
      },
      grid: {
        left: gridLeft,
        right: gridRight,
        bottom: 24,
        top: 48,
        containLabel: false,
      },
      xAxis: {
        type: "value",
        name: "Alertas",
        nameGap: 8,
        min: 0,
        max: xMax,
        axisLabel: { fontSize: compacto ? 11 : 12 },
        splitLine: { lineStyle: { color: "#eeeeee" } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: categorias,
        axisLabel: yAxisLabel,
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: "Alertas",
          type: "bar",
          data: valores,
          itemStyle: { color },
          barWidth: 16,
          barCategoryGap: "24%",
          label: {
            show: true,
            position: "right",
            distance: 6,
            fontSize: compacto ? 11 : 12,
            formatter: ({ value }) => (value > 0 ? value : ""),
          },
        },
      ],
    };
  }, [
    categorias,
    valores,
    titulo,
    color,
    compacto,
    yFontSize,
    yLabelWidth,
    yOverflow,
    xMax,
  ]);

  return <EchartsWrapper option={option} style={{ height }} id={id} />;
}
const MonitoreoAlertas = (props) => {
  const [monitoreo, setMonitoreo] = useState(props.monitoreo);
  const dataMesActualGestion = useMemo(() => {
    const m = monitoreo?.gestion?.mes_actual ?? {};
    return [
      { value: Number(m.gestionadas ?? 0), name: "Alertas gestionadas" },
      { value: Number(m.falsas ?? 0), name: "Falsa alerta" },
      { value: Number(m.sin_gestionar ?? 0), name: "Sin gestionar" },
      { value: Number(m.mal_gestionada ?? 0), name: "Mal gestionada" },
    ];
  }, [monitoreo]);
  const dataMesActualCalidad = useMemo(() => {
    const m = monitoreo?.calidad?.mes_actual ?? {};
    return [
      { value: Number(m.gestionadas ?? 0), name: "Alertas gestionadas" },
      { value: Number(m.falsas ?? 0), name: "Falsa alerta" },
      { value: Number(m.sin_gestionar ?? 0), name: "Sin gestionar" },
      { value: Number(m.mal_gestionada ?? 0), name: "Mal gestionada" },
    ];
  }, [monitoreo]);
  const semiDonutGestion = useMemo(
    () => ({
      color: ["#1976d2", "#ffa726", "#e0e0e0", "#ef5350"],
      tooltip: {
        trigger: "item",
        formatter: ({ name, value, percent }) =>
          `${name}<br/>${value} (${Number.isFinite(percent) ? percent : 0}%)`,
      },
      legend: { top: "4%", left: "center" },
      graphic: [
        {
          type: "text",
          left: "center",
          top: "82%",
          silent: true,
          style: {
            text: "Mes actual gestión",
            fill: "#424242",
            fontSize: 14,
            lineHeight: 18,
            width: 260,
            align: "center",
            overflow: "break",
            fontWeight: "bold",
          },
        },
      ],
      series: [
        {
          name: "Mes actual gestión",
          type: "pie",
          radius: ["48%", "72%"],
          center: ["50%", "72%"],
          startAngle: 180,
          endAngle: 360,
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          data: dataMesActualGestion,
        },
      ],
    }),
    [dataMesActualGestion]
  );

  const semiDonutCalidad = useMemo(
    () => ({
      color: ["#42a5f5", "#ffb74d", "#bdbdbd", "#e57373"],
      tooltip: {
        trigger: "item",
        formatter: ({ name, value, percent }) =>
          `${name}<br/>${value} (${Number.isFinite(percent) ? percent : 0}%)`,
      },
      legend: { top: "4%", left: "center" },
      graphic: [
        {
          type: "text",
          left: "center",
          top: "82%",
          silent: true,
          style: {
            text: "Mes actual calidad",
            fill: "#424242",
            fontSize: 14,
            lineHeight: 18,
            width: 260,
            align: "center",
            overflow: "break",
            fontWeight: "bold",
          },
        },
      ],
      series: [
        {
          name: "Mes actual calidad",
          type: "pie",
          radius: ["48%", "72%"],
          center: ["50%", "72%"],
          startAngle: 180,
          endAngle: 360,
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          data: dataMesActualCalidad,
        },
      ],
    }),
    [dataMesActualCalidad]
  );

  return (
    <Container fluid style={{ padding: 24 }}>
      {/* CSS rápido para el divisor vertical solo en md+ */}
      <style>
        {`
          @media (min-width: 768px) {
            .left-col {
              border-right: 1px solid #e0e0e0;
              padding-right: 12px;
            }
            .right-col {
              padding-left: 12px;
            }
          }
          .section-title {
            margin: 4px 0 12px 0;
            font-weight: 700;
            color: #424242;
            font-size: 16px;
          }
          .card {
            background: #fff;
            border-radius: 8px;
            padding: 12px;
            box-shadow: 0 1px 4px #eee;
          }
        `}
      </style>
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: 8,
          padding: 20,
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background: "#1976d2",
            color: "#fff",
            borderRadius: 4,
            padding: "4px 12px",
            fontWeight: "bold", 
            fontSize: "20px"
          }}
        >
          Monitoreo de Alertas
        </span>
        <span
          style={{
            background: "#e57373",
            color: "#fff",
            borderRadius: 4,
            padding: "4px 12px",
            fontWeight: "bold", 
            fontSize: "20px"
          }}
        >
          Cantidad total de alertas en la compañia: {monitoreo.total}
        </span>
      </div>
      <Row>
        <Col xs={12} md={6} className="left-col">
          <div
            className="section-title"
            style={{ fontWeight: "bold", fontSize: "20px" }}
          >
            Alertas de gestión : {monitoreo.total_gestion}
          </div>

          {monitoreo.total_gestion > 0 ? (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <GraficoDistribucion
                  datos={monitoreo?.gestion?.distribucion}
                  titulo="Distribución alertas de gestión"
                  etiquetas="wrap"
                  id="bar-distribucion-gestion"
                  height={320}
                />
              </div>
              <Row>
                <Col xs={12} md={6}>
                  <div className="card" style={{ marginBottom: 8 }}>
                    <GraficoBarrasHorizontal
                      datos={monitoreo?.gestion?.reincidencias}
                      titulo="Alertas gestión por meses de reincidencia"
                      color="#ec5353"
                      id="bar-horizontal-gestion-reincidencias"
                      compacto={true}
                      yLabelWidth={90}
                      yFontSize={10}
                      yOverflow="truncate"
                      height={300}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="card" style={{ marginBottom: 8 }}>
                    <EchartsWrapper
                      option={semiDonutGestion}
                      style={{ height: 300 }}
                      id="semi-donut-gestion"
                    />
                  </div>
                </Col>
              </Row>
            </>
          ) : null}
        </Col>

        <Col xs={12} md={6} className="right-col">
          <div
            className="section-title"
            style={{ fontWeight: "bold", fontSize: "20px" }}
          >
            Alertas de calidad : {monitoreo.total_calidad}
          </div>
          {monitoreo.total_calidad > 0 ? (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <GraficoDistribucion
                  datos={monitoreo?.calidad?.distribucion}
                  titulo="Distribución alertas de calidad"
                  etiquetas="wrap"
                  id="bar-distribucion-calidad"
                  height={320}
                />
              </div>
              <Row>
                <Col xs={12} md={6}>
                  <div className="card" style={{ marginBottom: 8 }}>
                    <GraficoBarrasHorizontal
                      datos={monitoreo?.calidad?.reincidencias}
                      titulo="Alertas calidad por meses de reincidencia"
                      color="#ec5353"
                      id="bar-horizontal-calidad-reincidencias"
                      compacto={true}
                      yLabelWidth={90} 
                      yFontSize={10} 
                      yOverflow="truncate" 
                      height={300}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="card" style={{ marginBottom: 8 }}>
                    <EchartsWrapper
                      option={semiDonutCalidad}
                      style={{ height: 300 }}
                      id="semi-donut-calidad"
                    />
                  </div>
                </Col>
              </Row>{" "}
            </>
          ) : null}
        </Col>
      </Row>
    </Container>
  );
};

export default MonitoreoAlertas;
